import { useCallback, useState } from 'react';

import { ArticleModuleHeroSidebar } from '#components/Article/Modules/ArticleModuleHeroSidebar';
import { ArticleModuleHeroSocialMediaSharer } from '#components/Article/Modules/ArticleModuleHeroSocialMediaSharer';
import { ArticleModuleMediaCollapsibleMetadataBox } from '#components/Article/Modules/ArticleModuleMediaCollapsibleMetadataBox';
import { ArticleHeroMedia, HERO_IMAGE_SIZES } from '#components/Article/Sections/ArticleHeroMedia';
import { ImageModule } from '#components/Image/ImageModule';
import { MediaContextPlaybackStatus } from '#cutils/context/MediaContext';
import { useCurrentDate } from '#cutils/context/TimeContext';
import { useDefaults } from '#cutils/hooks/useDefaults';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import type { ArticleData } from '#shared/types/article';
import { articleIsBreakingNews } from '#utils/article/articleIsBreakingNews';

import styles from './ArticleModuleHero.module.scss';

type Props = {
	article: ArticleData;
	onPlayStatusChange(status: MediaContextPlaybackStatus): void;
};

export function ArticleModuleHero({ article, onPlayStatusChange }: Props) {
	const currentDate = useCurrentDate();
	const [mediaPlaybackStatus, setMediaPlaybackStatus] = useState(MediaContextPlaybackStatus.STOPPED);
	const { fallbackTeaserModule } = useDefaults();

	const [teaserModule = fallbackTeaserModule] = article.teaserModule.nodes.filter(notNullOrUndefined);

	const handlePlayStatusChange = useCallback(
		(e: MediaContextPlaybackStatus) => {
			setMediaPlaybackStatus(e);
			onPlayStatusChange?.(e);
		},
		[onPlayStatusChange]
	);

	if (!teaserModule) {
		return null;
	}

	const commentId = article.allowComments ? article.rowId : undefined;

	const collapsibleMetadataBox = (
		<ArticleModuleMediaCollapsibleMetadataBox
			articleModule={teaserModule}
			className={styles.metadataBox}
			mediaPlaybackStatus={mediaPlaybackStatus}
			moduleType={teaserModule.type}
		/>
	);

	return (
		<>
			<div className={styles.wrapper}>
				<div className={styles.content}>
					{articleIsBreakingNews(article, currentDate) ? (
						<ImageModule image={article.image} isPriority={true} sizes={HERO_IMAGE_SIZES} />
					) : (
						<ArticleHeroMedia
							articlePublicationDate={article.publicationDate}
							teaserModule={teaserModule}
							isPriority={true}
							onPlayStatusChange={handlePlayStatusChange}
						/>
					)}
				</div>

				<ArticleModuleHeroSidebar mediaPlaybackStatus={mediaPlaybackStatus} article={article} />
			</div>

			{collapsibleMetadataBox}

			<ArticleModuleHeroSocialMediaSharer shareUrl={article.shareUrl} commentId={commentId} title={article.title} />
		</>
	);
}
